/**
 * Pomocník - používateľská príručka.
 *
 * Kapitoly sú Markdown súbory `content/<jazyk>/<kapitola>.md`, aby sa dali
 * upravovať bez zásahu do kódu. Prvý riadok `# Nadpis` je názov kapitoly
 * v obsahu. Obrázky odkazujú na `/help-img/{lang}/<meno>.jpg` - `{lang}` sa
 * nahradí jazykom kapitoly; screenshoty v `public/help-img/<jazyk>/` vyrába (nie `public/help/` - priečinok
 * s menom stránky by nginx pri `try_files $uri/` obslúžil namiesto aplikácie)
 * `scripts/help-screenshots.mjs` z ukážkovej školy.
 *
 * Kapitoly sa načítavajú až pri otvorení pomocníka a len v jednom jazyku -
 * do hlavného balíka aplikácie sa nedostanú.
 */
export const TOPICS = [
  'start',
  'calendar',
  'reservations',
  'clients',
  'payments',
  'instructors',
  'reports',
  'online',
  'emails',
  'settings',
] as const
export type Topic = (typeof TOPICS)[number]

export const HELP_LANGUAGES = ['sk', 'en', 'de', 'fr', 'it', 'pl', 'cs'] as const

const files = import.meta.glob('./content/*/*.md', {
  query: '?raw',
  import: 'default',
}) as Record<string, () => Promise<string>>

export interface Chapter {
  topic: Topic
  title: string
  markdown: string
}

/** Jazyk, v ktorom pomocník existuje - inak angličtina. */
export function helpLanguage(lang: string): string {
  const short = (lang || '').slice(0, 2)
  return (HELP_LANGUAGES as readonly string[]).includes(short) ? short : 'en'
}

/** Všetky kapitoly v jednom jazyku, v poradí obsahu. */
export async function loadChapters(lang: string): Promise<Chapter[]> {
  const language = helpLanguage(lang)
  return Promise.all(
    TOPICS.map(async (topic) => {
      const load = files[`./content/${language}/${topic}.md`] ?? files[`./content/en/${topic}.md`]
      const raw = load ? await load() : `# ${topic}`
      const markdown = raw.replaceAll('{lang}', language)
      const title = markdown.match(/^#\s+(.+)$/m)?.[1]?.trim() ?? topic
      return { topic, title, markdown }
    }),
  )
}

/** Text bez diakritiky a veľkých písmen - pre vyhľadávanie. */
export function fold(text: string) {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}
